import Alert from '@mui/material/Alert';
import { ClientDetailsForm } from '@src/clients/ClientDetailsForm';
import type { ClientFormSchema } from '@src/clients/schemas/ClientFormSchema';
import { Accordion } from '@src/common/components/Accordion';
import { Divider } from '@src/common/components/Divider';
import { Stack } from '@src/common/components/Stack';
import { theme } from '@src/common/styles/Theme';
import { jurisdictionIdsFields } from '@src/permitField/JurisdictionIdNumberFields';
import { useState } from 'react';
import type { Control, FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type * as yup from 'yup';

import { InsuranceForm } from './InsuranceForm';
import { JurisdictionIdNumbersForm } from './JurisdictionIdNumberForm';

interface Props<T extends FieldValues> {
  control: Control<T>;
  mailingIsSameAsPhysical: boolean;
  onMailingIsSameAsPhysicalChange: (value: boolean) => void;
  AdditionalDetails?: (props: { control: Control<T> }) => JSX.Element;
}

type AccordionState = {
  openClientDetails: boolean;
  openInsurance: boolean;
  openJurisdictionIdNumbers: boolean;
};

export const CompanyInformationForm = ({
  control,
  mailingIsSameAsPhysical,
  onMailingIsSameAsPhysicalChange,
  AdditionalDetails,
}: Props<yup.InferType<typeof ClientFormSchema>>) => {
  const [accordionState, setAccordionState] = useState<AccordionState>({
    openClientDetails: true,
    openInsurance: true,
    openJurisdictionIdNumbers: true,
  });
  const { t } = useTranslation();

  return (
    <>
      <Accordion
        id="company-info"
        title={t('clientDetails')}
        expanded={accordionState.openClientDetails}
        onChange={(_, expanded) =>
          setAccordionState({
            ...accordionState,
            openClientDetails: expanded,
          })
        }
      >
        <ClientDetailsForm
          control={control}
          onMailingIsSameAsPhysicalChange={onMailingIsSameAsPhysicalChange}
          mailingIsSameAsPhysical={mailingIsSameAsPhysical}
          AdditionalDetails={AdditionalDetails}
        />
      </Accordion>

      <Divider />

      <Accordion
        id="insurance"
        title={t('editClient.insuranceInformation')}
        expanded={accordionState.openInsurance}
        onChange={(_, expanded) =>
          setAccordionState({
            ...accordionState,
            openInsurance: expanded,
          })
        }
      >
        <InsuranceForm control={control} />
      </Accordion>

      <Divider />

      <Accordion
        id="jurisdictionsIdNumber"
        title={t('editClient.jurisdictionIdNumbers')}
        expanded={accordionState.openJurisdictionIdNumbers}
        onChange={(_, expanded) =>
          setAccordionState({
            ...accordionState,
            openJurisdictionIdNumbers: expanded,
          })
        }
      >
        <Stack direction="column" gap={theme.spacing['8']}>
          <Alert severity="info">{t('editClient.fieldsAreOptional')}</Alert>
          <JurisdictionIdNumbersForm
            control={control as unknown as Control}
            fields={Object.keys(jurisdictionIdsFields)}
            fieldsPrefix="jurisdictionIdNumbers"
          />
        </Stack>
      </Accordion>
    </>
  );
};
